/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import moment from "moment";
import jwt_decode from "jwt-decode";

import CloseIcon from "../assets/Close-Icon.svg";
import EditIcon from "@mui/icons-material/BorderColor";

import {
  capitalizeFirstLetterOfEachWord,
  formatValueByType,
  getCookie,
  sanitizeCurrency,
  sanitizePercentage,
} from "../utils/Common";
import {
  normalizeFeeDetailsConfig,
  convertStringToSnakeCase,
} from "../utils/Constants";
import { LoansContext } from "../context/LoansContext";
import { RootContext } from "../context/RootContext";
import {
  getTermSheetData,
  getSuggestedLoanTerms,
  formatDateForPDFName,
  isNotEmptyObject,
} from "../components/termSheet/Common";
import {
  generateNewTermSheet,
  getTermSheetVersions,
  getTermSheetBasedOnVersion,
  sendTermSheetEmail,
  addNewFieldConfig,
  updateFieldConfigBasedOnOriginator,
  getFieldConfigBasedOnOriginator,
  updateLatestTermSheetData,
  downloadTermsheetPdf,
  renameTermSheet,
} from "../services/termSheet";
import { useStyles } from "../components/termSheet/termSheet.style";
import { ObjectType } from "../types/tableTypes.js";
import { SizingTypeValue } from "../types/enums";
import { fetchRequestWaiver } from "../services/network";

type ParamType = {
  loanType: string | undefined;
  loanId: string | undefined;
  propType: string | undefined;
};

interface UseTermSheetActionsProps {
  evaluationID?: string | number;
  /**
   * `true` for the popover-driven instance (TermSheetForm): reacts to
   * `termSheetAction` context changes and builds the TermSheetV2 modal config.
   * `false` for a headless instance (e.g. LoansPagev2's autogenerate-on-submit
   * trigger) that must stay mounted regardless of the generate_term_sheet
   * permission, without reacting to popover-driven context changes — avoids
   * double-firing generate/regenerate/view/download when both instances are
   * mounted on the same page.
   */
  interactive?: boolean;
}

// Fires off the "add new custom fee" originator sync for a single fee:
// registers the field config, then (fire-and-forget, matching the original
// inline behavior) kicks off the per-originator field config update without
// awaiting/returning it. Extracted purely to keep handleAddNewFeeConfig's
// nesting shallow — behavior (including the fire-and-forget timing) is
// unchanged from the original inline `.then().then().catch()` chain.
function syncOriginatorFieldConfigForFee(
  fee: any,
  loanTypeToBePassed: string,
  headers: any,
  orgId: any,
  payload: any
) {
  addNewFieldConfig(
    {
      field: convertStringToSnakeCase(fee),
      dataType: "currency",
      loanType: loanTypeToBePassed,
    },
    headers
  ).then((res: any) => {
    updateFieldConfigBasedOnOriginator(orgId, payload, headers)
      .then((orgRes: any) => {
        return orgRes;
      })
      .catch((err: any) => {
        throw err;
      });
  });
}

// Mutates `fee.value` from newFeeDetails when the snake-cased keys match.
// Pulled out of the originatorAccountMappingData updater's `.map` to keep
// that updater's nesting shallow; logic (including the in-place mutation of
// `fee` before returning it) is unchanged from the original inline callback.
function applyFeeValueToOriginatorField(fee: any, newFeeDetails: any) {
  const snakeCaseKey = fee?.field;
  Object.keys(newFeeDetails).forEach((key: any) => {
    const snakeCaseKeyInDetails = convertStringToSnakeCase(key);
    if (snakeCaseKeyInDetails === snakeCaseKey) {
      fee.value = newFeeDetails[key];
    }
  });
  return fee;
}

// Deletes each of keysToDelete from every item in every sub-array of data,
// in place. Extracted from getTermSheetDataBasedOnAction's bplsizer-only
// cleanup block to keep that function's nesting shallow; logic is otherwise
// identical to the original triple-nested forEach.
function removeKeysFromDataRows(data: any[], keysToDelete: string[]) {
  data.forEach((subArray: any) => {
    subArray.forEach((item: any) => {
      keysToDelete.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(item, key)) {
          delete item[key];
        }
      });
    });
  });
}

/**
 * Single implementation of the term-sheet generate/save/email business logic,
 * shared by the interactive popover flow (TermSheetForm) and the headless
 * autogenerate-on-submit flow (LoansPagev2). See sprightly-beaming-wozniak
 * plan for why this used to be two divergent, duplicated implementations.
 */
export function useTermSheetActions({
  evaluationID = "",
  interactive = true,
}: UseTermSheetActionsProps) {
  const loanContextData = useContext(LoansContext);

  let originatorData: any =
    getCookie("originatorData") ||
    localStorage.getItem("originatorData") ||
    "{}";
  const orgId =
    loanContextData?.fetchData?.orgId ||
    loanContextData?.loanDetails?.orgId ||
    getCookie("orgId");

  originatorData = JSON.parse(originatorData);

  const emailBodyStr2 = useMemo(() => {
    return `<p>
      Dear (--- please enter name here ---),
    </p>
    <p>
      Thank you for the opportunity to provide financing on this property.  ${originatorData?.accountName} would consider structuring a loan subject to the following outline.
    </p>`;
  }, [originatorData]);

  // Borrower-name-aware default, used only for the autogenerate flow.
  const autoGenerateEmailBodyDefault = useMemo(() => {
    const borrowerName =
      loanContextData?.detailedLoanSizingData?.borrowerInformation
        ?.borrowerName ||
      loanContextData?.detailedLoanSizingData?.borrowerInformation
        ?.borrowingEntityName ||
      "--- please enter name here ---";
    return `<p>
      Dear ${borrowerName},
    </p>
    <p>
      Thank you for the opportunity to provide financing on this property.  ${originatorData?.accountName} would consider structuring a loan subject to the following outline.
    </p>`;
  }, [
    originatorData,
    loanContextData?.detailedLoanSizingData?.borrowerInformation,
  ]);

  const accessToken: string = getCookie("sizer_access_token") || "";
  const decodedData: ObjectType = jwt_decode(accessToken);

  const [openTermSheetMenu, setOpenTermSheetMenu] = useState<boolean>(false);
  const [defaultFeeDetails, setDefaultFeeDetails] = useState<any>([]);
  const [orgLevelFees, setOrgLevelFees] = useState<any>([]);
  const [orgLevelRequirements, setOrgLevelRequirements] = useState<any>([]);
  const [triggeredSendEmail, setTriggeredSendEmail] = useState<boolean>(false);
  const [isSendingEmail, setIsSendingEmail] = useState<boolean>(false);
  // Mirrors isSendingEmail for the sendEmail guard below. termSheetConfig
  // (and its footer.onSendEmail = sendEmail reference passed to the external
  // Send button) is only rebuilt when other, unrelated deps change — a plain
  // `if (isSendingEmail)` check inside sendEmail would read a stale snapshot
  // from whichever render's closure the button is still holding. A ref
  // mutates in place, so it's always current regardless of which closure
  // reads it.
  const isSendingEmailRef = useRef(false);

  const [isFooterEditEnabled, setIsFooterEditEnabled] =
    useState<boolean>(false);
  const [isEditsEnable, setIsEditsEnable] = useState<object>({
    isSubjectEditable: true,
    isFooterEditable: true,
  });
  const [signatureData, setSignatureData] = useState<any>({});
  const [isEmailSentOfCurrentVersion, setIsEmailSentOfCurrentVersion] =
    useState<boolean>(false);
  const [isTermsheetAlreadySaved, setIsTermsheetAlreadySaved] =
    useState<boolean>(false);
  const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);
  const [isMultiProperty, setIsMultiProperty] = useState<boolean>(false);
  const [isDataEdited, setIsDataEdited] = useState<boolean>(false);
  const [downloadDisclaimerText, setDownloadDisclaimerText] =
    useState<string>("");
  const [
    originatorsAccountMappingDetails,
    setOriginatorsAccountMappingDetails,
  ] = useState<any>({});
  const [latestPDF_URL, setLatestPDF_URL] = useState<string>("");
  const [originatorLogo, setOriginatorLogo] = useState<string>("");
  const [waiverRequest, setWaiverRequest] = useState<any>("");

  const {
    dynamicConfig,
    termSheetData,
    setTermSheetData,
    setTermSheetEmailData,
    setTermSheetLoanData,
    setTermSheetSuggestedLoanData,
    setTermSheetFeeData,
    setTermSheetRequirementsData,
    termSheetConfig,
    setTermSheetConfig,
    setTermSheetVersions,
    termSheetAction,
    showTermSheet,
    setTermSheetAction,
    currentTermSheetVersion,
    setCurrentTermSheetVersion,
    isOpenTermSheet,
    setIsOpenTermSheet,
    setSnackBarMessage,
    userRole,
    setOriginatorAccountMappingData,
  } = useContext(RootContext);

  const classes = useStyles();
  const { t } = useTranslation();
  const params = useParams<ParamType>();

  const { loanType, loanId } = params;

  const { latestTermSheetVersion, setLatestTermSheetVersion } =
    loanContextData ?? {};

  const [shouldRefetch, setShouldRefetch] = useState(false);

  async function getWaivers() {
    try {
      const waiverRequest = await fetchRequestWaiver([loanId]);
      setWaiverRequest(waiverRequest);
      return waiverRequest;
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {
    if (
      loanContextData?.propertyDetails?.length ||
      loanContextData?.loanDetails?.propertyDetails?.length
    ) {
      if (
        loanContextData?.propertyDetails?.length > 1 ||
        loanContextData?.loanDetails?.propertyDetails?.length > 1
      ) {
        setIsMultiProperty(true);
      } else {
        setIsMultiProperty(false);
      }
    }
  }, [loanContextData?.propertyDetails, loanContextData?.loanDetails]);

  useEffect(() => {
    getWaivers();
  }, []);

  const updateTermSheetFormData = (key: string, value: any) => {
    if (["emails", "emailBody", "feeDetails", "requirements"].includes(key)) {
      if (
        (termSheetAction === "edit" || isTermsheetAlreadySaved) &&
        !isDataEdited
      ) {
        setIsDataEdited(true);
      }
      setTermSheetData((state: any) => ({ ...state, [key]: value }));
    }
  };

  const handleFeeChange = (e: any, key: string, feeDetailsData: any) => {
    const tempFeeDetails = {
      ...JSON.parse(JSON.stringify(feeDetailsData)),
      [key]: e.target.value || " ",
    };
    updateTermSheetFormData("feeDetails", tempFeeDetails);
    setTermSheetFeeData(tempFeeDetails);
  };

  const handleFeeBlur = (e: any, key: string, feeDetailsData: any) => {
    let formattedValue: string;
    // Strip currency/percentage symbols before numeric conversion so
    // values like "$34.00" or "5%" are handled correctly.
    const rawValue = String(e).replace(/[^0-9.-]/g, "");

    if (rawValue === "" || isNaN(Number(rawValue))) {
      formattedValue = "$0.00";
    } else if (key === "Origination Points") {
      formattedValue = sanitizePercentage(Number(rawValue).toFixed(2));
    } else {
      formattedValue = sanitizeCurrency(Number(rawValue).toFixed(2));
    }
    const tempFeeDetails: any = {
      ...JSON.parse(JSON.stringify(feeDetailsData)),
      [key]: `${formattedValue}`,
    };
    updateTermSheetFormData("feeDetails", tempFeeDetails);
    setTermSheetFeeData(tempFeeDetails);
  };

  const handleSignatureSave = (data: any) => {
    setSignatureData(data);
  };

  const handleFooterChange = (e: any, key: string, termSheetData: any) => {
    switch (key) {
      case "name":
        break;
      case "email":
        break;
      case "accountName":
        break;
    }
  };

  const renameTermSheetHandler = async (name: string, version: any) => {
    const headers = {
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };
    try {
      const payload = {
        termSheetName: name,
      };
      await renameTermSheet(payload, loanId, version, headers);
      getTermSheetVersion();
    } catch (error) {
      console.error(error);
    }
  };

  // add new custom fee
  const handleAddNewFeeConfig = async (
    addedFees: any,
    newFees: any,
    newlyAddedFees: any,
    visibilityChangedFields: any
  ) => {
    const headers = {
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };

    const loanTypeToBePassed =
      loanType === "InvestorDSCR" ? "30 Year Loan" : "Bridge Loan";
    const termSheetDynamicFields: any = [];
    if (newlyAddedFees?.length) {
      newlyAddedFees?.forEach((fee: any) => {
        termSheetDynamicFields.push({
          op: "add",
          field: convertStringToSnakeCase(fee),
          loanType: loanTypeToBePassed,
          isActive: false,
        });
      });
      const payload = {
        sellers: [],
        loanTypes: [],
        termSheetDynamicFields,
      };

      await Promise.all(
        newlyAddedFees.map(async (fee: any) => {
          syncOriginatorFieldConfigForFee(
            fee,
            loanTypeToBePassed,
            headers,
            orgId,
            payload
          );
        })
      );
    }

    // changes for visibility
    const updatedFees = { ...newFees };
    if (visibilityChangedFields?.length) {
      const toBeUpdatedFields: any = [];
      visibilityChangedFields.forEach((field: any) => {
        if (!field.isVisible(userRole)) {
          delete updatedFees[field?.label];
        }
        toBeUpdatedFields.push({
          op: "replace",
          field: field?.id,
          loanType: loanTypeToBePassed,
          isActive: false,
        });
      });
      await updateFieldConfigBasedOnOriginator(
        orgId,
        {
          sellers: [],
          loanTypes: [],
          termSheetDynamicFields: toBeUpdatedFields,
        },
        headers
      );
    }

    setDefaultFeeDetails(
      addedFees?.filter((field: any) => field?.isVisible(userRole))
    );
    setOrgLevelFees(addedFees);
    updateTermSheetFormData("feeDetails", updatedFees);
  };

  // delete fee
  const handleDeleteFee = async (key: any, feeDetailsData: any) => {
    try {
      const headers = {
        "kc-realm": dynamicConfig?.ViteAppKcRealm,
      };
      const loanTypeToBePassed =
        loanType === "InvestorDSCR" ? "30 Year Loan" : "Bridge Loan";

      const newFeeDetails: any = {
        ...JSON.parse(JSON.stringify(feeDetailsData)),
      };
      delete newFeeDetails[key];
      const toBeUpdatedFields: any = [
        {
          op: "update",
          field: key,
          loanType: loanTypeToBePassed,
          isActive: false,
        },
      ];
      await updateFieldConfigBasedOnOriginator(
        orgId,
        {
          sellers: [],
          loanTypes: [],
          termSheetDynamicFields: toBeUpdatedFields,
        },
        headers
      );
      updateTermSheetFormData("feeDetails", newFeeDetails);
      setTermSheetFeeData({ ...newFeeDetails });
    } catch (err) {
      console.error(err);
    }
  };

  const getLoanData = (loanData: any) => {
    const returnData: any = {};
    Object.keys(loanData).forEach((key) => {
      const value =
        typeof loanData[key] === "string" &&
        (loanData[key]?.includes(",") || loanData[key]?.includes("$"))
          ? loanData[key]?.replace(/[$,]/g, "")
          : loanData[key];
      returnData[key] = value;
    });
    return returnData;
  };

  const getTermsheetDateTimeStampName = () => {
    const date = new Date();
    const formattedName =
      `${t("termSheetKeyForHeader")} v ` +
      `${(date.getMonth() + 1).toString().padStart(2, "0")}-` +
      `${date.getDate().toString().padStart(2, "0")}-` +
      `${date.getFullYear()},` +
      `${date.getHours().toString().padStart(2, "0")}:` +
      `${date.getMinutes().toString().padStart(2, "0")}`;
    return formattedName;
  };

  const getGenarateTermSheetPayload = (
    loanData: any,
    defaultFees: any,
    tempTermSheetData: any,
    suggestedTerms: any
  ) => {
    const termSheetInfo = [
      ...loanData.flat(),
      ...(suggestedTerms?.length > 0 ? suggestedTerms.flat() : []),
    ].reduce((acc: any, obj: any) => {
      return { ...acc, ...obj };
    }, {});

    const newPropertyAddressDetails: any = {};
    tempTermSheetData?.propertyAddressDetails?.map((item: any) => {
      newPropertyAddressDetails[item?.label] = item?.value;
    });
    const payload = {
      termSheetLoanInfo: {
        ...getLoanData(termSheetInfo),
      },
      signatureInfo: {
        ...signatureData,
      },
      feeDetails: defaultFees,
      requirements: tempTermSheetData?.requirements?.filter(
        (r: string) => r !== ""
      ),
      emailSentOn: loanData.emailSentOn,
      isAutoGenerated: tempTermSheetData?.isAutoGenerated,
      termSheetName: termSheetData?.termSheetName
        ? termSheetData?.termSheetName
        : getTermsheetDateTimeStampName(),
      propertyAddressDetails: newPropertyAddressDetails,
      email: {
        from: termSheetConfig?.email?.fromEmail,
        to: tempTermSheetData?.emails?.toEmails?.length
          ? tempTermSheetData?.emails?.toEmails
          : [],
        cc: tempTermSheetData?.emails?.ccEmails?.length
          ? tempTermSheetData?.emails?.ccEmails
          : [],
        subject:
          tempTermSheetData?.emails?.subject ?? termSheetConfig?.email?.subject,
        content: tempTermSheetData?.emailBody ?? emailBodyStr2,
        date:
          moment(termSheetConfig?.email?.date, "MM/DD/YYYY").format(
            "YYYY-MM-DD"
          ) ?? moment().format("YYYY-MM-DD"),
      },
    };
    return { ...payload };
  };

  // create-update  - api
  const generateOrUpdateTermSheet = async (
    loanData: any,
    defaultFees: any,
    tempTermSheetData: any,
    suggestedTerms: any,
    mode: string
  ) => {
    try {
      const realmHeaders = {
        "kc-realm": dynamicConfig?.ViteAppKcRealm,
      };
      const payload = getGenarateTermSheetPayload(
        loanData,
        defaultFees,
        tempTermSheetData,
        suggestedTerms
      );
      if (mode === "create") {
        const { data } = await generateNewTermSheet(
          payload,
          loanId,
          realmHeaders
        );
        setCurrentTermSheetVersion(data?.data?.termSheetVersion);
      } else {
        const version = latestTermSheetVersion;
        await updateLatestTermSheetData(payload, loanId, realmHeaders, version);
      }

      setIsOpenTermSheet(true);
      setOpenTermSheetMenu(false);
    } catch (err) {
      console.error(err);
    }
  };

  // get all term sheet versions
  const getTermSheetVersion = async () => {
    try {
      const realmHeaders = {
        "kc-realm": dynamicConfig?.ViteAppKcRealm,
      };
      const termSheetVersions = await getTermSheetVersions(
        realmHeaders,
        loanId
      );
      const sortedTermSheetVersions = termSheetVersions?.data?.data.sort(
        (a: any, b: any) => {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        }
      ) ?? [];
      setLatestTermSheetVersion(sortedTermSheetVersions[0]?.termSheetVersion);
      setTermSheetVersions(sortedTermSheetVersions);
      // Only the interactive (popover-driven) instance needs to refresh the
      // displayed modal data here — the headless autogenerate instance has no
      // modal to populate, and doing this unconditionally would make it
      // silently overwrite the shared termSheetData context with unrelated
      // "latest version" data on every mount and after every save.
      if (interactive && sortedTermSheetVersions?.length) {
        fetchLatestTermSheetData(sortedTermSheetVersions[0]?.termSheetVersion);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLatestTermSheetData = async (version: string) => {
    const loanTypeStr =
      loanContextData?.loanDetails?.productType ||
      loanContextData?.fetchData?.productType;
    const response: any = await fetchGetTermSheetBasedOnVersion(version);
    const data = getTermSheetData(
      loanTypeStr,
      loanContextData,
      t,
      termSheetAction,
      response,
      loanContextData?.defaultSelectedDataView,
      evaluationID,
      loanId,
      waiverRequest
    );
    if (response?.emailSentOn) {
      setIsEmailSentOfCurrentVersion(true);
      setIsTermsheetAlreadySaved(false);
    } else {
      setIsEmailSentOfCurrentVersion(false);
      setIsTermsheetAlreadySaved(true);
    }
    const newSuggestedLoanTerms = getSuggestedLoanTerms(
      loanTypeStr,
      loanContextData,
      t,
      termSheetAction,
      response
    );
    const newRequirements = response?.requirements || [""];
    const newFeeDetails =
      response?.feeDetails ||
      JSON.parse(JSON.stringify(termSheetData?.feeDetails));
    const ccEmails = response?.email?.cc;
    const fromEmail = response?.email?.from;
    const toEmails = response?.email?.to;
    const emailSentOn = response?.emailSentOn;
    const emailBody = response?.email?.content;
    const subject = response?.email?.subject;
    setTermSheetData({
      emailSentOn,
      loanData: data,
      suggestedLoanTerms: newSuggestedLoanTerms,
      requirements: newRequirements || [""],
      feeDetails: newFeeDetails,
      emailBody: emailBody ?? emailBodyStr2,
      emails: {
        fromEmail,
        toEmails,
        ccEmails,
        subject,
      },
      currentTermSheetVersion: version,
      evaluationID,
    });

    setOriginatorAccountMappingData((previous: any) => {
      const loanTypeStr =
        loanType === "InvestorDSCR" ? "30 Year Loan" : "Bridge Loan";
      return {
        ...previous,
        termSheetDynamicFields: previous?.termSheetDynamicFields
          ?.filter((item: any) => item?.loanType === loanTypeStr)
          ?.map((fee: any) => applyFeeValueToOriginatorField(fee, newFeeDetails)),
      };
    });
  };

  const getEmailPayload = (tempTermSheetData: any) => {
    const loanDataFields: any = [];
    const suggestedDataFields: any = [];

    tempTermSheetData?.loanData?.map((loanInfo: any) => {
      Object.keys(loanInfo?.[0] ?? {})?.map((key: any) => {
        loanDataFields.push({
          label: key,
          value: loanInfo?.[0]?.[key],
        });
      });
    });

    tempTermSheetData?.suggestedLoanTerms?.map((loanInfo: any) => {
      Object.keys(loanInfo?.[0] ?? {})?.map((key: any) => {
        suggestedDataFields.push({
          label: key,
          value: loanInfo?.[0]?.[key],
        });
      });
    });

    const feeDetailsFormat = Object.keys(tempTermSheetData?.feeDetails)?.map(
      (fee: any) => {
        const feeConfig = orgLevelFees?.find((el: any) => el?.label === fee);
        const dataType =
          feeConfig?.dataType === "percentage"
            ? "percentageWithout100x"
            : feeConfig?.dataType;
        return {
          label: fee,
          value: formatValueByType(
            tempTermSheetData?.feeDetails?.[fee],
            dataType
          ),
        };
      }
    );

    const payload: any = {
      templateData: {
        subject:
          tempTermSheetData?.emails?.subject ?? termSheetConfig?.email?.subject,
        originatorLogo: originatorLogo,
        emailBody: tempTermSheetData?.emailBody ?? emailBodyStr2,
        loanType: loanType === "InvestorDSCR" ? "dscr" : "bridge",
        totalPoints:
          loanContextData?.loanDetails?.loanEconomics
            ?.totalOrgAndDiscountPoints ?? null,
        loanDetails: {
          dynamicFields: loanDataFields,
        },
        suggestedLoanTerms: {
          dynamicFields: suggestedDataFields,
        },
        propertyAddressDetails: tempTermSheetData?.propertyAddressDetails,
        feeDetails: {
          dynamicFields: feeDetailsFormat,
        },
        requirements:
          tempTermSheetData?.requirements?.filter((r: string) => r !== "") ||
          [],
        viewLoan: window.location.href,
        originator: {
          fullName: signatureData?.name || signatureData?.accountName,
          email: signatureData?.email || "",
          originatorName: signatureData?.accountName || "",
          phone: signatureData?.phone || "",
        },
        termSheetName: tempTermSheetData?.termSheetName,
      },
      sendTo: tempTermSheetData?.emails?.toEmails?.length
        ? tempTermSheetData?.emails?.toEmails
        : [],
      sendCc: tempTermSheetData?.emails?.ccEmails?.length
        ? tempTermSheetData?.emails?.ccEmails
        : [],
      sender: termSheetConfig?.email?.fromEmail,
    };
    return { ...payload };
  };

  // Core email-send API call, shared by the interactive sendEmail handler and
  // the autogenerate flow (which always sends, silently, no toEmails guard).
  const sendTermSheetEmailCore = async (tempTermSheetData: any) => {
    const headers = {
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };
    const payload = getEmailPayload(tempTermSheetData);
    payload.attachment = [
      {
        path: latestPDF_URL,
        filename: `TermSheet_${evaluationID}_${formatDateForPDFName(
          new Date()
        )}.pdf`,
      },
    ];
    payload.templateData.smtpIp = originatorsAccountMappingDetails.smtpIp;
    payload.templateData.passKey = originatorsAccountMappingDetails.smtpIp;
    if (!latestPDF_URL) {
      const url = await downloadTermsheetHandler(tempTermSheetData, false);
      payload.attachment = [
        {
          path: url,
          filename: `TermSheet_${evaluationID}_${formatDateForPDFName(
            new Date()
          )}.pdf`,
        },
      ];
      setLatestPDF_URL(url);
    }
    await sendTermSheetEmail(payload, headers, dynamicConfig);
    setIsEmailSentOfCurrentVersion(true);
  };

  // email notification api (interactive: "send email" footer action)
  const sendEmail = async (
    tempTermSheetData: any,
    isTermsheetAlreadySaved: boolean
  ) => {
    // Guards against the Send button being clicked again while a previous
    // click is still in flight (see isSendingEmailRef above for why this
    // can't just check the isSendingEmail state).
    if (isSendingEmailRef.current) {
      return;
    }
    if (tempTermSheetData?.emails?.toEmails?.length === 0) {
      setTriggeredSendEmail(true);
      return;
    }

    isSendingEmailRef.current = true;
    setIsSendingEmail(true);
    try {
      await sendTermSheetEmailCore(tempTermSheetData);
      if (["generate", "regenerate", "edit"]?.includes(termSheetAction)) {
        const mode =
          termSheetAction === "edit" || isTermsheetAlreadySaved
            ? "update"
            : "create";
        await saveOrUpdateNewTermSheet(tempTermSheetData, mode, true);
      } else {
        setIsOpenTermSheet(false);
        const messages: Record<string, string> = {
          generate: "Term Sheet Generated And Saved Successfully.",
          regenerate: "Term Sheet Regenerated And Saved Successfully.",
          edit: "Term Sheet Generated And Saved Successfully.",
        };

        const message = messages[termSheetAction] ?? "";

        setSnackBarMessage(message);
        getTermSheetVersion();
        setTermSheetAction("");
        setIsTermsheetAlreadySaved(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      isSendingEmailRef.current = false;
      setIsSendingEmail(false);
    }
  };

  const saveOrUpdateNewTermSheet = async (
    tempTermSheetData: any,
    mode: string,
    isEmailSent: boolean,
    actionOverride?: string
  ) => {
    const action = actionOverride ?? termSheetAction;
    try {
      const loanTypeStr =
        loanContextData?.loanDetails?.productType ||
        loanContextData?.fetchData?.productType;
      const data: any = getTermSheetData(
        loanTypeStr,
        loanContextData,
        t,
        action,
        tempTermSheetData,
        loanContextData?.defaultSelectedDataView,
        evaluationID,
        loanId,
        waiverRequest
      );
      const suggestedTerms = getSuggestedLoanTerms(
        loanTypeStr,
        loanContextData,
        t,
        action,
        tempTermSheetData
      );
      const feeDetailsObj: any = tempTermSheetData?.feeDetails;
      if (isEmailSent) {
        data.emailSentOn = moment().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
      }
      await generateOrUpdateTermSheet(
        data,
        feeDetailsObj,
        tempTermSheetData,
        suggestedTerms,
        mode
      );
      // Whatever "Origination Fee" ends up saved on the term sheet becomes
      // the loan's recorded origination fee — write it back into Loan
      // Economics (in-memory; persists whenever the user next explicitly
      // saves the application, same as any other Loan Economics field).
      // Guarded on detailedLoanSizingData existing since quick-sizing loans
      // don't have this field at all. Uses updateDetailedLoanSizingData (not
      // the raw setter) to match this codebase's established convention —
      // it keeps detailedLoanSizingDataRef in sync, which other code (e.g.
      // BuyDownOptionsWrapper.tsx, pages/Loans/v1/Common.tsx) reads directly
      // to avoid stale-closure reads of this same data.
      const originationFeeValue = feeDetailsObj?.["Origination Fee"];
      if (
        loanContextData?.detailedLoanSizingData &&
        originationFeeValue != null
      ) {
        loanContextData.updateDetailedLoanSizingData?.({
          ...loanContextData.detailedLoanSizingData,
          loanEconomics: {
            ...loanContextData.detailedLoanSizingData?.loanEconomics,
            originationFee: String(originationFeeValue).replace(/[$,]/g, ""),
          },
        });
      }
      if (isEmailSent) {
        setIsOpenTermSheet(false);
        setTermSheetAction("");
        setIsTermsheetAlreadySaved(false);
      }
      const message = getSnackbarMessage(mode, isEmailSent, action);
      setSnackBarMessage(message);

      getTermSheetVersion();
    } catch (error) {
      console.error(error);
      setIsOpenTermSheet(false);
      setTermSheetAction("");
    }
  };

  const getSnackbarMessage = (
    mode: string,
    isEmailSent: boolean,
    action: string
  ) => {
    if (!isEmailSent) {
      return mode !== "update"
        ? "Term Sheet Saved Successfully."
        : "Changes Saved Successfully.";
    }

    if (isEmailSent) {
      if (action === "generate" || action === "edit") {
        return "Term Sheet Generated And Saved Successfully.";
      } else if (action === "regenerate" || action === "edit") {
        return "Term Sheet Regenerated And Saved Successfully.";
      }
    }

    return ""; // Default case if no conditions match
  };

  //save TermSheet button handler
  const saveTermSheetHandler = async (
    tempTermSheetData: any,
    isTermsheetAlreadySaved: boolean
  ) => {
    let mode: string = "create";
    if (termSheetAction === "edit") {
      mode = "update";
    }
    if (
      (termSheetAction === "generate" || termSheetAction === "regenerate") &&
      isTermsheetAlreadySaved
    ) {
      mode = "update";
    }
    saveOrUpdateNewTermSheet(tempTermSheetData, mode, false);
    setIsTermsheetAlreadySaved(true);
  };

  // on change of emails
  const handleEmailChange = (
    toEmails: any,
    ccEmails: any,
    tempTermSheetData: any
  ) => {
    const emails = {
      ...tempTermSheetData.emails,
      toEmails: [...toEmails],
      ccEmails: [...ccEmails],
    };
    updateTermSheetFormData("emails", emails);
  };

  const emailBodyChange = (val: string) => {
    updateTermSheetFormData("emailBody", val);
  };

  // "Origination Fee" (distinct from the "Origination Points" percentage fee)
  // is entered directly on the Loan Economics tab
  // (detailedLoanSizingData.loanEconomics.originationFee, a raw numeric
  // string). Priority: the originator's configured default for the current
  // loan type (if the API happens to have one — opportunistic, not
  // required, since the fee itself is always shown regardless), else the
  // Loan Economics value, else "$0.00".
  // NOTE: the external TermSheetV2 package (compiled, no source here) only
  // renders a fee row when `feeConfig.isVisible() && feeDetailsData[label]`
  // — i.e. it requires a TRUTHY value, not just isVisible() being true. A
  // `null`/empty fallback would make "Origination Fee" silently disappear
  // whenever neither source has a value, defeating "always visible" — so
  // the fallback here must be a truthy placeholder, not null.
  const resolveOriginationFeeValue = (orgDefaultValue: any) => {
    if (orgDefaultValue) {
      return sanitizeCurrency(orgDefaultValue);
    }
    const localValue =
      loanContextData?.detailedLoanSizingData?.loanEconomics?.originationFee;
    if (localValue) {
      return sanitizeCurrency(localValue);
    }
    return "$0.00";
  };

  const getFeeDetails = () => {
    const feeDetailsObj: any = {};
    defaultFeeDetails?.forEach((obj: any) => {
      if (obj?.isVisible(userRole)) {
        const field = orgLevelFees?.find(
          (fee: any) => fee?.label === obj?.label
        );
        let formattedValue;

        if (obj?.label === "Origination Fee") {
          formattedValue = resolveOriginationFeeValue(field?.defaultValue);
        } else if (field?.defaultValue) {
          if (obj?.label === "Origination Points") {
            formattedValue = sanitizePercentage(field?.defaultValue);
          } else {
            formattedValue = sanitizeCurrency(field?.defaultValue);
          }
        } else {
          formattedValue = "$0.00";
        }

        feeDetailsObj[obj?.label] = formattedValue;
      }
    });
    return { ...feeDetailsObj };
  };

  // Format required Adress String
  const cleanedAddressString = (propertyLocation: any) => {
    if (!propertyLocation) {
      return "";
    }
    const pattern = new RegExp(
      `\\b(${propertyLocation.postalCode}|${propertyLocation.city}|${propertyLocation.state})\\b`,
      "g"
    );
    // Remove matches from the address
    const cleanedAddress = propertyLocation.address
      ?.replace(pattern, "")
      ?.trim()
      ?.replace(/\s+/g, " ");
    const address = propertyLocation?.address
      ? `${cleanedAddress}, ${propertyLocation?.city}, ${propertyLocation?.state} ${propertyLocation?.postalCode}`
      : `${propertyLocation?.city}, ${propertyLocation?.state} ${propertyLocation?.postalCode}`;
    return address;
  };

  // Shared by the "generate" branch and the "regenerate" sub-case of the
  // existing-version branch below: for multi-property loans, this table's
  // row-level "Property Address" key is redundant with the property list
  // shown separately, so it's stripped from `data` in place and replaced
  // by a synthesized `propertyAddressDetails` list. Extracted verbatim
  // from the (previously duplicated) original inline block.
  const computeMultiPropertyAddressDetailsAndStripFromData = (
    data: any,
    propertyDetailsSource: any
  ) => {
    const propertyAddressDetails = propertyDetailsSource?.map(
      (prop: any, index: number) => ({
        label: `Property ${index + 1}`,
        value: cleanedAddressString(prop.propertyLocation),
      })
    );
    // Iterate over the data and remove the "Property Address" key if it exists
    data.forEach((subArray: any) => {
      subArray.forEach((item: any) => {
        if (Object.prototype.hasOwnProperty.call(item, "Property Address")) {
          delete item["Property Address"];
        }
      });
    });
    return propertyAddressDetails;
  };

  // Branch of getTermSheetDataBasedOnAction for action === "generate" (or
  // "autogenerate" with no existing version yet) — builds a fresh term
  // sheet snapshot from the current loan/waiver data. Extracted purely to
  // keep getTermSheetDataBasedOnAction's cognitive complexity within
  // Sonar's limits; behavior (including all side effects on `data`) is
  // unchanged from the original inline block.
  const buildGenerateActionData = (
    action: string,
    loanTypeStr: any,
    newWaiverRequest: any
  ) => {
    const data = getTermSheetData(
      loanTypeStr,
      loanContextData,
      t,
      action,
      termSheetData,
      loanContextData?.defaultSelectedDataView,
      evaluationID,
      loanId,
      newWaiverRequest
    );
    const newSuggestedLoanTerms = getSuggestedLoanTerms(
      loanTypeStr,
      loanContextData,
      t,
      termSheetData
    );
    const feeDetailsObj: any = getFeeDetails();

    let propertyAddressDetails: any = null;
    if (isMultiProperty) {
      const propertyDetails = loanContextData?.propertyDetails?.length
        ? loanContextData?.propertyDetails
        : loanContextData?.loanDetails?.propertyDetails;
      propertyAddressDetails = computeMultiPropertyAddressDetailsAndStripFromData(
        data,
        propertyDetails
      );
    }

    return {
      data,
      newSuggestedLoanTerms,
      newRequirements: [...orgLevelRequirements],
      newFeeDetails: feeDetailsObj,
      emailBody: action === "autogenerate" ? autoGenerateEmailBodyDefault : emailBodyStr2,
      currentVersion: `${(+latestTermSheetVersion || 0) + 1}`,
      isAutoGenerated: action === "autogenerate",
      termSheetName: getTermsheetDateTimeStampName(),
      propertyAddressDetails,
      ccEmails: [] as any,
      toEmails: [] as any,
      emailSentOn: null as any,
      subject: null as string | null,
      date: moment(new Date()).format("MM/DD/YYYY"),
    };
  };

  const resolveIsAutoGeneratedForVersionResponse = (
    action: string,
    response: any
  ): boolean => {
    if (action === "view") {
      return response?.isAutoGenerated;
    }
    if (action === "autogenerate") {
      return true;
    }
    return false;
  };

  // regenerate/autogenerate produce a fresh snapshot of current data
  // (unlike view/edit/download, which show exactly what was saved) — so
  // "Origination Fee" always re-syncs from Loan Economics here, rather
  // than carrying forward whatever was saved on a prior version. For
  // view/edit/download, only BACKFILL it when the key is missing entirely
  // — term sheets saved before this fee existed have no "Origination Fee"
  // key at all in their persisted feeDetails, and the external
  // TermSheetV2 package only renders a fee row when
  // feeDetailsData[label] is truthy, so without this backfill it would
  // silently stay invisible on any pre-existing term sheet forever, even
  // though the fee is always configured as visible. A value the user
  // actually edited and saved is left alone — this only fills in a gap,
  // never overwrites it.
  const withOriginationFeeBackfill = (
    newFeeDetails: any,
    action: string,
    originationFeeDefaultValue: any
  ) => {
    if (!newFeeDetails) {
      return newFeeDetails;
    }
    if (["regenerate", "autogenerate"].includes(action)) {
      return {
        ...newFeeDetails,
        "Origination Fee": resolveOriginationFeeValue(originationFeeDefaultValue),
      };
    }
    if (newFeeDetails["Origination Fee"] == null) {
      return {
        ...newFeeDetails,
        "Origination Fee": resolveOriginationFeeValue(originationFeeDefaultValue),
      };
    }
    return newFeeDetails;
  };

  const resolveTermSheetResponseDate = (response: any): string => {
    if (response?.email?.date) {
      return moment(new Date(response.email.date)).format("MM/DD/YYYY");
    }
    if (response?.updatedAt) {
      return moment(new Date(response.updatedAt)).format("MM/DD/YYYY");
    }
    return "";
  };

  // Sub-branch of the existing-version branch below for when
  // fetchGetTermSheetBasedOnVersion actually returned a saved term sheet.
  // Extracted purely to keep getTermSheetDataBasedOnAction's cognitive
  // complexity within Sonar's limits; behavior (including the
  // setSignatureData side effect) is unchanged from the original inline
  // block.
  const buildVersionResponseActionData = (
    action: string,
    loanTypeStr: any,
    newWaiverRequest: any,
    originatorData: any,
    response: any
  ) => {
    const data = getTermSheetData(
      loanTypeStr,
      loanContextData,
      t,
      action,
      response,
      loanContextData?.defaultSelectedDataView,
      evaluationID,
      loanId,
      newWaiverRequest
    );
    const newSuggestedLoanTerms = getSuggestedLoanTerms(
      loanTypeStr,
      loanContextData,
      t,
      action,
      response
    );

    const isAutoGenerated = resolveIsAutoGeneratedForVersionResponse(action, response);
    const newRequirements = response?.requirements || [""];
    const originationFeeDefaultValue = orgLevelFees?.find(
      (f: any) => f?.label === "Origination Fee"
    )?.defaultValue;
    const rawNewFeeDetails =
      response?.feeDetails ||
      JSON.parse(JSON.stringify(termSheetData?.feeDetails));
    const newFeeDetails = withOriginationFeeBackfill(
      rawNewFeeDetails,
      action,
      originationFeeDefaultValue
    );
    const ccEmails = response?.email?.cc;
    const toEmails = response?.email?.to;
    const emailSentOn = response?.emailSentOn;
    const emailBody = response?.email?.content;
    const subject =
      action === "regenerate"
        ? defaultSubjectCheck()
        : response?.email?.subject;
    const currentVersion = response?.termSheetVersion;
    const termSheetName = ["regenerate", "autogenerate"].includes(action)
      ? getTermsheetDateTimeStampName()
      : response?.termSheetName;
    let propertyAddressDetails =
      response?.propertyAddressDetails &&
      Object.keys(response?.propertyAddressDetails)?.map((key: any) => {
        return {
          label: key,
          value: response?.propertyAddressDetails[key],
        };
      });
    const date = resolveTermSheetResponseDate(response);

    if (isMultiProperty && action === "regenerate") {
      const propertyDetails = loanContextData?.propertyDetails?.length
        ? loanContextData?.propertyDetails
        : loanContextData?.loanDetails?.propertyDetails;
      propertyAddressDetails = computeMultiPropertyAddressDetailsAndStripFromData(
        data,
        propertyDetails
      );
    }
    setSignatureData(
      isNotEmptyObject(response?.signatureInfo)
        ? response?.signatureInfo
        : {
            name: decodedData?.name,
            email: decodedData?.email,
            accountName: originatorData?.accountName,
            phone: decodedData?.phone,
          }
    );

    return {
      data,
      newSuggestedLoanTerms,
      newRequirements,
      newFeeDetails,
      isAutoGenerated,
      ccEmails,
      toEmails,
      emailSentOn,
      emailBody,
      subject,
      currentVersion,
      termSheetName,
      propertyAddressDetails,
      date,
    };
  };

  // Sub-branch of the existing-version branch below for when
  // fetchGetTermSheetBasedOnVersion came back empty — falls back to
  // whatever is already in termSheetData. Extracted purely to keep
  // getTermSheetDataBasedOnAction's cognitive complexity within Sonar's
  // limits; behavior is unchanged from the original inline block.
  const buildFallbackToSavedTermSheetActionData = (
    action: string,
    loanTypeStr: any,
    newWaiverRequest: any
  ) => {
    const data = getTermSheetData(
      loanTypeStr,
      loanContextData,
      t,
      action,
      termSheetData,
      loanContextData?.defaultSelectedDataView,
      evaluationID,
      loanId,
      newWaiverRequest
    );
    const newSuggestedLoanTerms = getSuggestedLoanTerms(
      loanTypeStr,
      loanContextData,
      t,
      action,
      termSheetData
    );
    let newFeeDetails =
      termSheetData?.feeDetails ||
      JSON.parse(JSON.stringify(termSheetData?.feeDetails));
    if (newFeeDetails && newFeeDetails["Origination Fee"] == null) {
      newFeeDetails = {
        ...newFeeDetails,
        "Origination Fee": resolveOriginationFeeValue(
          orgLevelFees?.find((f: any) => f?.label === "Origination Fee")
            ?.defaultValue
        ),
      };
    }

    return {
      data,
      newSuggestedLoanTerms,
      newRequirements: termSheetData?.requirements || [""],
      newFeeDetails,
      ccEmails: termSheetData?.email?.cc,
      toEmails: termSheetData?.email?.to,
      emailSentOn: termSheetData?.emailSentOn,
      emailBody: termSheetData?.email?.content,
      subject: termSheetData?.email?.subject,
      date: termSheetData?.date,
      propertyAddressDetails: termSheetData?.propertyAddressDetails,
      isAutoGenerated: termSheetData?.isAutoGenerated,
      termSheetName: termSheetData?.termSheetName,
      currentVersion: "" as string | number,
    };
  };

  // Branch of getTermSheetDataBasedOnAction for "regenerate" / "view" /
  // "download" / "edit" / "autogenerate" (with an existing version) —
  // loads the saved term sheet for currentTermSheetVersion and derives
  // display data from it, falling back to termSheetData when that fetch
  // comes back empty, and leaving everything at its original default when
  // there's no currentTermSheetVersion at all. Extracted purely to keep
  // getTermSheetDataBasedOnAction's cognitive complexity within Sonar's
  // limits; behavior is unchanged from the original inline block.
  const buildExistingVersionActionData = async (
    action: string,
    loanTypeStr: any,
    newWaiverRequest: any,
    originatorData: any
  ) => {
    if (!currentTermSheetVersion) {
      return {
        data: [] as any,
        newSuggestedLoanTerms: [] as any,
        newRequirements: [""] as any,
        newFeeDetails: {} as any,
        isAutoGenerated: false,
        ccEmails: [] as any,
        toEmails: [] as any,
        emailSentOn: null as any,
        emailBody: null as any,
        subject: null as string | null,
        currentVersion: "" as string | number,
        termSheetName: "",
        propertyAddressDetails: null as any,
        date: moment(new Date()).format("MM/DD/YYYY"),
      };
    }

    const response: any = await fetchGetTermSheetBasedOnVersion(
      currentTermSheetVersion
    );
    if (response) {
      return buildVersionResponseActionData(
        action,
        loanTypeStr,
        newWaiverRequest,
        originatorData,
        response
      );
    }
    return buildFallbackToSavedTermSheetActionData(
      action,
      loanTypeStr,
      newWaiverRequest
    );
  };

  // save termSheet data based on term sheet action
  const getTermSheetDataBasedOnAction = async (actionOverride?: string) => {
    const action = actionOverride ?? termSheetAction;
    if (action === "rename") {
      return;
    }
    const fromEmail: any = originatorsAccountMappingDetails?.fromEmail
      ? originatorsAccountMappingDetails?.fromEmail
      : "noreply@bplsizer.com";

    const originatorData: any = JSON.parse(
      getCookie("originatorData") || "{}"
    );
    const loanTypeStr =
      loanContextData?.loanDetails?.productType ||
      loanContextData?.fetchData?.productType;
    let newWaiverRequest: any = waiverRequest;
    if (["generate", "regenerate", "autogenerate"]?.includes(action)) {
      newWaiverRequest = await getWaivers();
    }

    let data: any;
    let newRequirements: any;
    let newFeeDetails: any;
    let newSuggestedLoanTerms: any;
    let currentVersion: string | number;
    let toEmails: any;
    let ccEmails: any;
    let emailSentOn: any;
    let emailBody: any;
    let subject: string | null;
    let date: string;
    let propertyAddressDetails: any;
    let termSheetName: string;
    let isAutoGenerated: boolean;

    if (
      ["generate"].includes(action) ||
      (action === "autogenerate" && !latestTermSheetVersion)
    ) {
      ({
        data,
        newSuggestedLoanTerms,
        newRequirements,
        newFeeDetails,
        emailBody,
        currentVersion,
        isAutoGenerated,
        termSheetName,
        propertyAddressDetails,
        ccEmails,
        toEmails,
        emailSentOn,
        subject,
        date,
      } = buildGenerateActionData(action, loanTypeStr, newWaiverRequest));
    } else if (
      ["regenerate", "view", "download", "edit", "autogenerate"].includes(
        action
      )
    ) {
      ({
        data,
        newSuggestedLoanTerms,
        newRequirements,
        newFeeDetails,
        isAutoGenerated,
        ccEmails,
        toEmails,
        emailSentOn,
        emailBody,
        subject,
        currentVersion,
        termSheetName,
        propertyAddressDetails,
        date,
      } = await buildExistingVersionActionData(
        action,
        loanTypeStr,
        newWaiverRequest,
        originatorData
      ));
    } else {
      // td: get from api and add it here
      data = [];
      newRequirements = [""];
      newFeeDetails = {
        ...JSON.parse(JSON.stringify(termSheetData?.feeDetails ?? "{}")),
      };
      newSuggestedLoanTerms = [];
      currentVersion = "";
      toEmails = [];
      ccEmails = [];
      emailSentOn = null;
      emailBody = null;
      subject = null;
      date = moment(new Date()).format("MM/DD/YYYY");
      propertyAddressDetails = null;
      termSheetName = "";
      isAutoGenerated = false;
    }
    if (["generate", "regenerate", "edit"].includes(action)) {
      setIsOpenTermSheet(true);
      setOpenTermSheetMenu(false);
      if (["generate", "regenerate"].includes(action)) {
        setLatestTermSheetVersion((+latestTermSheetVersion || 0) + 1);
      } else {
        setLatestTermSheetVersion(currentTermSheetVersion);
      }
    }

    setTermSheetEmailData({
      fromEmail,
      toEmails,
      ccEmails,
    });
    setTermSheetLoanData(data);
    setTermSheetSuggestedLoanData(newSuggestedLoanTerms);
    setTermSheetFeeData(newFeeDetails);
    setTermSheetRequirementsData(newRequirements || [""]);
    if (dynamicConfig?.ViteAppKcRealm === "bplsizer") {
      const keysToDelete = ["Discount Points (%)", "Discount Points ($)"];
      removeKeysFromDataRows(data, keysToDelete);
    }
    const newOrgLevelFees = orgLevelFees?.map((item: any) => {
      const findFee = Object.prototype.hasOwnProperty.call(
        newFeeDetails,
        item.label
      );
      return {
        ...item,
        isVisible: () => (findFee ? true : item?.isVisible()),
      };
    });
    setOrgLevelFees(newOrgLevelFees);
    const newTermSheetData = {
      emailSentOn,
      loanData: data,
      suggestedLoanTerms: newSuggestedLoanTerms,
      requirements: newRequirements || [""],
      feeDetails: newFeeDetails,
      emailBody:
        emailBody ??
        (action === "autogenerate" ? autoGenerateEmailBodyDefault : emailBodyStr2),
      emails: {
        fromEmail,
        toEmails,
        ccEmails,
        subject,
        smtpIp: originatorsAccountMappingDetails.smtpIp,
        passKey: originatorsAccountMappingDetails.smtpIp,
      },
      date,
      currentTermSheetVersion: currentVersion,
      propertyAddressDetails,
      evaluationID,
      termSheetName,
      isAutoGenerated,
    };
    setTermSheetData(newTermSheetData);
    if (action === "autogenerate") {
      try {
        await saveOrUpdateNewTermSheet(
          newTermSheetData,
          "create",
          true,
          "autogenerate"
        );
        await sendTermSheetEmailCore(newTermSheetData);
        setSnackBarMessage("Term Sheet Generated And Saved Successfully.");
      } catch (err) {
        console.error(err);
        setIsOpenTermSheet(false);
        setTermSheetAction("");
      }
    }
  };

  useEffect(() => {
    setShouldRefetch(
      ["generate", "regenerate", "edit"].includes(termSheetAction)
    );
  }, [loanContextData?.currentSelectedInterestRate, termSheetAction]);

  const handleSubjectChange = (subjectValue: any, tempTermSheetData: any) => {
    const emails = {
      ...tempTermSheetData.emails,
      subject: subjectValue,
    };
    updateTermSheetFormData("emails", emails);
  };

  const defaultSubjectCheck = () => {
    if (isMultiProperty) {
      return "Loan Proposal for Multiple Properties";
    }
    let addressStr;

    if (
      loanContextData?.loanDetails?.sizingType ===
      SizingTypeValue?.DETAILED_LOAN_SIZING
    ) {
      addressStr = loanContextData?.detailedLoanSizingData ?? {};
    } else if (
      loanContextData?.loanDetails?.sizingType ===
      SizingTypeValue?.QUICK_LOAN_SIZING
    ) {
      addressStr = loanContextData?.quickLoanSizingData ?? {};
    } else {
      addressStr = loanContextData;
    }

    const address = cleanedAddressString(
      addressStr?.propertyDetails?.[0]?.propertyLocation ??
        addressStr?.propertyLocation
    );
    return `Loan Proposal for ${address}`;
  };

  // set termsheet config (interactive-only: builds the props consumed by the
  // TermSheetV2 modal, and reacts to view/download side effects. The headless
  // instance never opens this modal, so it never needs to build this config.)
  useEffect(() => {
    if (!interactive) return;
    const date = moment(new Date()).format("MM/DD/YYYY");
    const originatorData: any = JSON.parse(
      getCookie("originatorData") || "{}"
    );
    const defaultSubject = defaultSubjectCheck();

    const versionToBeDisplayed =
      "regenerate" == termSheetAction || "generate" == termSheetAction
        ? latestTermSheetVersion
        : currentTermSheetVersion;
    const termSheetName = termSheetData?.termSheetName
      ? termSheetData?.termSheetName
      : `${t("termSheetKeyForHeader")} v ${versionToBeDisplayed ?? ""}`;
    const baseData = {
      isViewMode: !["regenerate", "generate", "edit"].includes(termSheetAction),
      termSheetAction,
      originatorLogo: originatorLogo,
      header: {
        showOriginatorInfo: true,
        originatorInfo: `${originatorData?.accountName} - ID: ${evaluationID} (${termSheetName})`,
        className: classes?.termSheetHeader,
        closeIcon: CloseIcon,
      },
      email: {
        subject: defaultSubject,
        date,
        fromEmail: originatorsAccountMappingDetails?.fromEmail
          ? originatorsAccountMappingDetails?.fromEmail
          : "noreply@bplsizer.com",
        handleEmailChange,
        handleSubjectChange,
        emailBody: emailBodyStr2,
      },
      feeDetails: {
        config: orgLevelFees?.length > 0 ? orgLevelFees : defaultFeeDetails,
        orgLevelFees,
        handleFeeChange,
        handleFeeBlur,
        handleDeleteFee,
        handleAddNewFeeConfig,
      },
      footer: {
        onCloseModal: setIsOpenTermSheet,
        onSendEmail: sendEmail,
        onSaveTermSheet: saveTermSheetHandler,
        name: decodedData?.name,
        handleFooterChange,
        isFooterEditEnabled,
        setIsFooterEditEnabled,
        EditIcon,
        handleDownloadPDF,
      },
      termSheetVersion: currentTermSheetVersion,
      showWaterMark:
        currentTermSheetVersion !== latestTermSheetVersion ||
        (termSheetAction === "view" && termSheetData?.isAutoGenerated),
      updateTermSheetFormData,
      isEditsEnable,
      signatureInfo: signatureData,
      handleSignatureSave,
      capitalizeFirstLetterOfEachWord,
      userRole,
    };

    const newData = { ...baseData };

    switch (termSheetAction) {
      case "view":
        setIsOpenTermSheet(true);
        setOpenTermSheetMenu(false);
        break;
      case "download":
        if (isNotEmptyObject(signatureData || {})) {
          setOpenTermSheetMenu(false);
          downloadTermsheetHandler(termSheetData);
        }
        break;
      default:
        break;
    }

    setTermSheetConfig(newData);
  }, [
    interactive,
    evaluationID,
    t,
    defaultFeeDetails,
    termSheetAction,
    currentTermSheetVersion,
    signatureData,
    isEditsEnable,
    orgLevelFees,
  ]);

  const downloadTermsheetHandler = async (
    data: any,
    download: boolean | undefined = true
  ) => {
    const headers = {
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };
    const payload = await getEmailPayload(data);
    payload.templateData.templateName = "los_generic_term_sheet.ftl";
    payload.templateData.fileName = `TermSheet_${evaluationID}_${formatDateForPDFName(
      new Date()
    )}`;
    payload.templateData.originatorLogoBgColor = "";
    const pdf = await downloadTermsheetPdf(
      { ...payload?.templateData },
      headers
    );
    if (download) {
      const link = document.createElement("a");
      link.href = pdf.data;
      setLatestPDF_URL(pdf.data);
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      return pdf.data;
    }
  };

  const setFeeDetailsConfig = async () => {
    const headers = {
      "kc-realm": dynamicConfig?.ViteAppKcRealm,
    };
    try {
      const { data } = await getFieldConfigBasedOnOriginator(orgId, headers);
      setOriginatorAccountMappingData({ ...data });
      setOriginatorLogo(data?.originatorLogo);
      const defaultRequirements = data?.requirement?.map(
        (req: any) => req?.data
      );
      setOriginatorsAccountMappingDetails({
        fromEmail: data?.fromEmail,
        smtpIp: data?.smtpIp,
        passKey: data?.passKey,
      });
      if (defaultRequirements && defaultRequirements?.length > 0) {
        setOrgLevelRequirements([...defaultRequirements]);
      }
      const config: any = normalizeFeeDetailsConfig(data, loanType, userRole);
      // "Origination Fee" is hardcoded on the frontend and always shown,
      // regardless of whether the originator config has a matching-loanType
      // termSheetDynamicFields entry (unlike every other fee here, which is
      // fully API-driven). If the API *does* have one for the current loan
      // type, normalizeFeeDetailsConfig will have already produced it —
      // that entry's defaultValue is preserved so resolveOriginationFeeValue
      // can still prefer it; otherwise a synthetic always-visible entry is
      // added with no defaultValue, so resolveOriginationFeeValue falls
      // through to the Loan Economics value instead.
      const apiOriginationFee = config?.find(
        (f: any) => f?.label === "Origination Fee"
      );
      const otherFees = config?.filter(
        (f: any) => f?.label !== "Origination Fee"
      ) ?? [];
      const finalConfig = [
        ...otherFees,
        {
          id: "originationFee",
          label: "Origination Fee",
          isVisible: () => true,
          isEditable: () => true,
          isCurrency: true,
          dataType: "currency",
          defaultValue: apiOriginationFee?.defaultValue,
          isActive: true,
        },
      ];
      setOrgLevelFees(finalConfig);
      setDefaultFeeDetails(
        finalConfig?.filter((field: any) => field?.isVisible(userRole))
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (termSheetAction === "") {
      setDefaultFeeDetails(
        orgLevelFees?.filter((field: any) => field?.isVisible(userRole))
      );
    }
    if (termSheetAction === "generate") {
      const originatorData: any = JSON.parse(
        getCookie("originatorData") || "{}"
      );
      setSignatureData({
        name: decodedData?.name,
        email: decodedData?.email,
        accountName: originatorData?.accountName,
        phone: "",
      });
      if (isTermsheetAlreadySaved) {
        setIsTermsheetAlreadySaved(false);
      }
    }
    const enabledPermission = ["generate", "regenerate", "edit"]?.includes(
      termSheetAction
    )
      ? true
      : false;
    setIsEditsEnable((previous) => ({
      ...previous,
      isSubjectEditable: enabledPermission,
      isFooterEditable: enabledPermission,
    }));
  }, [termSheetAction]);

  // get term sheet data based on term sheet action (interactive only: the
  // headless instance triggers getTermSheetDataBasedOnAction via a direct
  // call, never by watching termSheetAction context, to avoid double-firing
  // generate/regenerate/edit/view/download when both instances are mounted).
  useEffect(() => {
    if (!interactive) return;
    if (termSheetAction === "") {
      setLatestPDF_URL("");
    }
    if (
      ((openTermSheetMenu || termSheetAction === "regenerate") &&
        termSheetAction &&
        showTermSheet &&
        !isOpenTermSheet) ||
      (shouldRefetch &&
        ["generate", "regenerate", "edit"].includes(termSheetAction) &&
        isOpenTermSheet)
    ) {
      getTermSheetDataBasedOnAction();
      setShouldRefetch(false);
    }
  }, [
    interactive,
    termSheetAction,
    openTermSheetMenu,
    showTermSheet,
    defaultFeeDetails,
    shouldRefetch,
  ]);

  // get term sheet versions on load
  useEffect(() => {
    loanId && getTermSheetVersion();
    setFeeDetailsConfig();
  }, []);

  useEffect(() => {
    setTriggeredSendEmail(false);
  }, [termSheetData?.emails?.toEmails]);

  // get term sheet data based on version
  const fetchGetTermSheetBasedOnVersion = async (
    termSheetVersion = currentTermSheetVersion
  ) => {
    try {
      const realmHeaders = {
        "kc-realm": dynamicConfig?.ViteAppKcRealm,
      };
      const response = await getTermSheetBasedOnVersion(
        realmHeaders,
        loanId,
        +termSheetVersion
      );
      return response?.data?.data;
    } catch (err) {
      console.error(err);
    }
  };

  const handleDownloadPDF = async (
    isDataEdited: boolean,
    isTermsheetAlreadySaved: boolean
  ) => {
    let disclaimerText = "";
    if (!isTermsheetAlreadySaved) {
      disclaimerText = `You haven't saved your Termsheet yet. Would you like to save it before downloading?`;
    } else if (isDataEdited) {
      disclaimerText = `Changes you made to the Termsheet have not been saved. Would you like to save it before downloading?`;
    } else {
      downloadTermsheetHandler(termSheetData);
      return; // Exit early if no disclaimer
    }
    setShowDisclaimer(true);
    setDownloadDisclaimerText(disclaimerText);
  };

  const containedButtonHandler = async () => {
    const mode = isTermsheetAlreadySaved ? "update" : "create";
    const isEmailSent = false;
    await saveOrUpdateNewTermSheet(termSheetData, mode, isEmailSent);
    if (isDataEdited) {
      setIsDataEdited(false);
    }
    downloadTermsheetHandler(termSheetData);
    setShowDisclaimer(false);
  };

  const outlinedButtonHandler = () => {
    downloadTermsheetHandler(termSheetData);
    setShowDisclaimer(false);
  };

  const closeDownloadDisclaimer = () => {
    setShowDisclaimer(false);
  };

  return {
    openTermSheetMenu,
    setOpenTermSheetMenu,
    isEmailSentOfCurrentVersion,
    renameTermSheetHandler,
    isTermsheetAlreadySaved,
    setIsTermsheetAlreadySaved,
    isDataEdited,
    setIsDataEdited,
    isSendingEmail,
    triggeredSendEmail,
    emailBodyChange,
    showDisclaimer,
    downloadDisclaimerText,
    containedButtonHandler,
    outlinedButtonHandler,
    closeDownloadDisclaimer,
    getTermSheetVersion,
    setShouldRefetch,
    getTermSheetDataBasedOnAction,
  };
}
